import React, { useState, useRef } from "react";
import { needsMfa, stashPendingMfa } from "../utils/mfa";
import { toast } from "react-hot-toast";
import { apiUrl } from "@/lib/api";
import { facebookStartUrl } from "@/lib/oauthNonce";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import { useMsal } from "@azure/msal-react";
import { GOOGLE_CLIENT_ID, microsoftLoginRequest, FACEBOOK_LOGIN_ENABLED } from "@/config/oauth";
import { isAppleSignInAvailable, signInWithApple } from "@/utils/appleSignIn";
import { storeAuthTokens } from "@/utils/postAuth";
import { isInAppBrowser } from "@/utils/browserEnv";
import { trackEvent } from "@/lib/tracker";
import { metaTrack, gaTrack, signupAudience } from "@/lib/analytics";

// Apple / Google / Microsoft one-tap auth. The backend endpoints create-or-
// login, so the same component powers both "Sign in" and quick "Sign up".
// On success it stores tokens and calls onAuthed(data); the parent decides
// where to route. `verb` swaps the button label ("Sign up" vs "Sign in").
export default function SocialAuthButtons({ onAuthed, verb = "Sign in", signup, disabled = false, onPopupBlocked, accountType }) {
  const [busy, setBusy] = useState(false);
  // When social sign-in can't even start (blocker eats Google's script, popup
  // never opens, undetected in-app browser), the button silently no-ops and
  // real prospects bounce (confirmed in the clickstream data). Hold a plain-
  // language reason so we can nudge them to email instead of a dead end.
  const [blockedMsg, setBlockedMsg] = useState("");
  const { instance: msalInstance } = useMsal();
  const anyDisabled = busy || disabled;
  // Facebook/Instagram (and similar) in-app browsers block popup OAuth, so Google
  // and Microsoft sign-in silently fail there. Hide them and fall back to email.
  const inAppBrowser = isInAppBrowser();

  // Until now every social-auth failure produced only a toast + console.error,
  // so Google/Microsoft/Apple signups that died were invisible to the funnel
  // (signup_failed fired on the email path only). Emit an event on failure so
  // these show up on the Clickstream dashboard with a cause attached.
  //   stage "script" -> the provider SDK script never loaded (blocker/network)
  //   stage "popup"  -> the popup never opened / errored before auth
  //   stage "oauth"  -> provider rejected the auth (access_denied, bad token…)
  //   stage "backend"-> our /oauth/<provider>/ verify call failed
  /* Whether this is a signup, stated rather than sniffed out of the label.
     It used to be inferred with /sign\s*up/i against `verb`, which tied the
     analytics to the wording: relabelling the buttons "Continue with Facebook"
     would have silently stopped `signup_failed` firing for every social
     signup, and flipped the error copy to talk about signing in. Callers can
     now say which it is; the old inference stays as the default so nothing
     that does not pass it changes behaviour. */
  const isSignup = signup ?? /sign\s*up/i.test(verb);
  const emailWord = isSignup ? "Sign up with your email below instead." : "Sign in with your email below instead.";
  // Plain-language, per-cause messages for the stages where nothing visibly
  // happened — these are the ones worth catching the user before they bounce.
  const BLOCKED_MESSAGES = {
    script: `Google sign-in couldn't load: a browser extension or your network may be blocking it. ${emailWord}`,
    popup: `The sign-in window couldn't open, often a pop-up blocker or an in-app browser. ${emailWord}`,
  };
  const trackAuthFailure = (provider, stage, reason) => {
    const meta = { provider, stage, method: "oauth", reason: String(reason || "").slice(0, 160) };
    trackEvent("oauth_error", meta);
    // Mirror onto the funnel metric the team already watches, but only when this
    // instance is a sign-UP surface (the same component also powers sign-in).
    if (isSignup) trackEvent("signup_failed", meta);
    // Nothing opened -> tell them why and steer them to the email form.
    if (BLOCKED_MESSAGES[stage]) {
      setBlockedMsg(BLOCKED_MESSAGES[stage]);
      onPopupBlocked?.();
    }
  };

  /* Which side of the marketplace this instance was rendered for. Drives both
     what gets sent to the server (so the account is built the right shape) and
     which buttons appear at all. */
  const isBusiness = ["organization", "org", "business", "company"].includes(
    String(accountType || "").toLowerCase()
  );

  const post = async (path, body) => {
    const res = await fetch(apiUrl(path), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
      credentials: "omit",
      mode: "cors",
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.error || "Sign-in failed.");
    }
    return res.json();
  };

  // The single emitter of account_created for every OAuth path.
  //
  // It was not always. RegisterPage, TalentLanding and BusinessLanding each
  // fired their own account_created from onAuthed as well, so a social signup
  // on those three surfaces counted twice. Worse, the two copies disagreed:
  // this one wrote `method: "google"`, theirs wrote `method: "oauth"` with no
  // provider at all, so the dashboards (which filter on method="oauth") saw
  // only the copy that could not name a provider. That is why all ten OAuth
  // signups on record read `provider: None` and Facebook could not be told
  // from Google.
  //
  // The provider is known here and nowhere else, so the event belongs here.
  // `method` is now always "oauth" (what the funnel queries filter on) and the
  // provider rides in its own field. Parents pass `accountType` down and get
  // the provider back through onAuthed(data, provider) for their own extras.
  const finish = (data, provider) => {
    // Two-step sign-in on: the sign-in page asks for the code.
    if (needsMfa(data)) {
      stashPendingMfa(data);
      window.location.assign("/auth/login");
      return;
    }
    storeAuthTokens(data);
    if (data?.is_new_user) {
      const meta = { method: "oauth", provider, account_type: accountType };
      trackEvent("account_created", meta);
      gaTrack("sign_up", meta);
      metaTrack("CompleteRegistration", signupAudience(accountType));
    }
    onAuthed?.(data, provider);
  };

  const handleGoogle = async (tokenResponse) => {
    setBusy(true);
    try {
      finish(await post("/api/accounts/oauth/google/", { access_token: tokenResponse.access_token, account_type: accountType }), "google");
    } catch (e) {
      trackAuthFailure("google", "backend", e?.message);
      toast.error(e?.message || "Google sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleApple = async () => {
    setBusy(true);
    try {
      const { identity_token, first_name, last_name } = await signInWithApple();
      finish(await post("/api/accounts/oauth/apple/", { identity_token, first_name, last_name, account_type: accountType }), "apple");
    } catch (e) {
      const msg = String(e?.message || "");
      if (/cancel/i.test(msg) || e?.code === "1001") return; // user dismissed
      trackAuthFailure("apple", "oauth", msg);
      toast.error(msg || "Apple sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleMicrosoft = async () => {
    setBusy(true);
    try {
      const loginResponse = await msalInstance.loginPopup(microsoftLoginRequest);
      finish(await post("/api/accounts/oauth/microsoft/", { access_token: loginResponse.accessToken, id_token: loginResponse.idToken, account_type: accountType }), "microsoft");
    } catch (e) {
      if (e?.errorCode === "user_cancelled") return;
      trackAuthFailure("microsoft", e?.errorCode ? "oauth" : "popup", e?.errorCode || e?.message);
      toast.error(e?.message || "Microsoft sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  /* Facebook, and why it is the only one that survives an in-app browser.

     Google and Microsoft are hidden below when `inAppBrowser` is true, because
     their flows open a popup and a WKWebView will not. That is not a small
     exclusion: 87% of the people who start signing up here arrive inside
     Meta's in-app browser, so for almost everyone this component renders
     nothing at all and the only route is email and a password.

     This one is a full-page redirect to our own backend, which redirects on to
     Facebook. No popup, no SDK, nothing for an embedded browser to block. So
     it is deliberately NOT gated on `inAppBrowser` -- it is the button that
     exists for that case. And the audience is already logged into Facebook,
     because Facebook is where they came from. */
  const startFacebook = () => {
    trackEvent("oauth_started", { provider: "facebook", method: "redirect" });
    /* Carry the audience across the redirect.
       The popup providers hand their result back to this component, which knows
       which side of the marketplace the button was rendered for. Facebook does
       not come back here at all -- it lands on /auth/social, which has only a
       token and no idea whether it just made a company or a jobseeker. So that
       screen reported every Facebook signup with no account_type, and since
       Facebook is the ONLY social route inside Meta's in-app browser, that hole
       sat directly under the traffic most likely to be paid for.
       sessionStorage rather than the `next` param: same tab, same origin,
       survives the round trip, and does not put anything in a URL. */
    try {
      window.sessionStorage.setItem("sift_oauth_audience", accountType || "");
    } catch { /* private mode: falls back to the old no-audience behaviour */ }
    window.location.href = facebookStartUrl(window.location.pathname + window.location.search);
  };

  return (
    <div className="space-y-3">
      {blockedMsg && (
        <div
          role="alert"
          className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
        >
          {blockedMsg}
        </div>
      )}
      {/* Order, and who sees what.

          Google first, then Microsoft: those are the two a company recognises
          as a work login, and the company side is the funnel being fixed.
          Apple follows because iOS requires it be offered wherever other
          social sign-in is.

          Facebook is individual-only as of 2026-09-14. It stays for talent
          because it is the ONLY one-tap option that works inside Meta's
          in-app browser -- `inAppBrowser` hides Google and Microsoft there,
          since their popups silently fail -- and that browser carries most of
          the ad traffic. It is not a work login, and on the company side it
          was the button a company pressed on its way to a jobseeker account.

          The consequence, stated rather than discovered: a company inside
          Meta's in-app browser now sees no social buttons at all, and gets
          the email form. That is the deliberate trade. */}
      {GOOGLE_CLIENT_ID && !inAppBrowser && (
        <GoogleOAuthProvider
          clientId={GOOGLE_CLIENT_ID}
          // The GSI script itself getting blocked (ad/privacy extension, network)
          // is the silent killer: without it, clicking the button does nothing at
          // all. Catch it so we can say so and route them to email.
          onScriptLoadError={() => trackAuthFailure("google", "script", "gsi_script_load_error")}
        >
          <GoogleButton
            onSuccess={handleGoogle}
            onFailure={(stage, reason) => trackAuthFailure("google", stage, reason)}
            disabled={anyDisabled}
            verb={verb}
          />
        </GoogleOAuthProvider>
      )}

      {!inAppBrowser && (
      <button
        type="button"
        onClick={handleMicrosoft}
        disabled={anyDisabled}
        className="w-full min-h-[44px] h-12 flex items-center justify-center gap-3 border border-line-strong rounded-pill font-medium text-ink bg-surface hover:border-brand hover:text-brand active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true">
          <rect x="1" y="1" width="9" height="9" fill="#f25022" />
          <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
          <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
          <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
        </svg>
        {verb} with Microsoft
      </button>
      )}

      {isAppleSignInAvailable() && (
        <button
          type="button"
          onClick={handleApple}
          disabled={anyDisabled}
          className="w-full min-h-[44px] h-12 flex items-center justify-center gap-3 rounded-pill font-medium text-white bg-black hover:bg-gray-900 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg width="18" height="18" viewBox="0 0 384 512" fill="white" aria-hidden="true">
            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C53.2 141.6 0 184.5 0 270.5c0 25.4 4.7 51.6 14 78.5 12.6 35.5 58 122.6 105.4 121.1 24.8-.6 42.3-17.6 74.5-17.6 31.3 0 47.5 17.6 76.4 17.6 47.8-.7 88.8-79.8 100.8-115.4-64.1-30.2-60.8-88.5-60.8-90zM256.3 91.2c30.2-35.8 27.4-68.4 26.5-80.2-26.6 1.5-57.4 18.1-74.9 38.6-19.3 22-30.6 49.3-28.2 80.1 28.8 2.2 55.1-12.6 76.6-38.5z" />
          </svg>
          {verb} with Apple
        </button>
      )}

      {FACEBOOK_LOGIN_ENABLED && !isBusiness && (
        <button
          type="button"
          onClick={startFacebook}
          disabled={anyDisabled}
          className="w-full min-h-[44px] h-12 flex items-center justify-center gap-3 rounded-pill font-medium text-white bg-[#1877F2] hover:bg-[#166FE5] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white" aria-hidden="true">
            <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.96h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
          </svg>
          {verb} with Facebook
        </button>
      )}
    </div>
  );
}

// Google needs the useGoogleLogin hook, which must live inside
// GoogleOAuthProvider — hence a small inner component.
function GoogleButton({ onSuccess, onFailure, disabled, verb }) {
  // Set the moment any real outcome (success/error/non-oauth-error) fires, so
  // the "did a popup ever open?" watchdog below can tell a silent no-op apart
  // from a flow that's actually in progress.
  const settledRef = useRef(false);
  const login = useGoogleLogin({
    flow: "implicit",
    onSuccess: (tokenResponse) => {
      settledRef.current = true;
      onSuccess({ access_token: tokenResponse.access_token });
    },
    // Surface the real reason instead of a generic message. `error` /
    // error_description come straight from Google (e.g. "access_denied" when the
    // OAuth consent screen is in Testing mode and the user isn't a test user, or
    // an origin/redirect mismatch). Logging it turns "it just doesn't work" into
    // a precise, fixable diagnosis.
    onError: (err) => {
      settledRef.current = true;
      console.error("Google OAuth error:", err);
      const reason = err?.error_description || err?.error || "";
      onFailure?.("oauth", reason || "google_oauth_error");
      toast.error(reason ? `Google sign-in failed: ${reason}` : "Google sign-in failed.");
    },
    // Fires when the popup can't even open or the user closes it (popup blocked,
    // network, GSI init failure) — distinct from an OAuth-level rejection above.
    onNonOAuthError: (err) => {
      settledRef.current = true;
      console.error("Google OAuth non-OAuth error:", err);
      if (err?.type === "popup_closed") return; // user dismissed; not an error
      onFailure?.("popup", err?.type || "popup_failed_to_open");
      toast.error("Google sign-in couldn't start. Check pop-up blocker and try again.");
    },
  });

  // The invisible failure our data shows: the button is clicked, no popup ever
  // opens, and no callback fires (GSI script blocked, or the popup swallowed by
  // a blocker). Detect it by focus: a real popup steals focus from our window
  // within a moment. If ~1.6s after the click our window still holds focus and
  // nothing has settled, treat it as blocked and route them to email.
  const handleClick = () => {
    settledRef.current = false;
    let blurred = false;
    const onBlur = () => { blurred = true; };
    window.addEventListener("blur", onBlur, { once: true });
    login();
    window.setTimeout(() => {
      window.removeEventListener("blur", onBlur);
      if (!settledRef.current && !blurred && document.hasFocus()) {
        onFailure?.("popup", "no_popup_focus");
      }
    }, 1600);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className="w-full min-h-[44px] h-12 flex items-center justify-center gap-3 border border-line-strong rounded-pill font-medium text-ink bg-surface hover:border-brand hover:text-brand active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.0 24.0 0 0 0 0 21.56l7.98-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      </svg>
      {verb} with Google
    </button>
  );
}
